import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Switch,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Skeleton,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { formatDate } from '../utils/formatDate';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import ConfigurationWarning from '../components/common/ConfigurationWarning';

// Validation schema
const schedulerSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  description: Yup.string().nullable(),
  schedule: Yup.string().nullable(), // Make schedule optional since it can be derived from cronExpression
  syncType: Yup.string().required('Sync type is required').nullable(),
  subSyncType: Yup.string().oneOf(['whole', 'updated'], 'Sub sync type must be whole or updated').required('Sub sync type is required').nullable(),
  direction: Yup.string().required('Direction is required').nullable(),
  isActive: Yup.boolean().default(true)
});

export default function Schedulers() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState(null);
  const [isRunning, setIsRunning] = useState(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const [changingStatus, setChangingStatus] = useState(null);
  const [allTypesUsed, setAllTypesUsed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // State for tracking form changes
  const [originalFormValues, setOriginalFormValues] = useState({});
  const [hasFormChanges, setHasFormChanges] = useState(false);

  // State for schedule options
  const [scheduleOptions, setScheduleOptions] = useState({});
  const [currentScheduleOptions, setCurrentScheduleOptions] = useState([]);

  // Fetch schedulers
  const { data: schedulers = [], isLoading } = useQuery({
    queryKey: ['schedulers'],
    queryFn: async () => {
      try {
        const response = await api.get('/schedulers');

        // Handle different possible response structures
        const schedulersData = Array.isArray(response.data) ? response.data :
          response.data?.data ? response.data.data :
            response.data?.status === 'success' ? response.data.data : [];

        // Ensure each scheduler has default values
        return schedulersData.map(scheduler => ({
          id: scheduler.id || '',
          name: scheduler.name || 'Unnamed Scheduler',
          description: scheduler.description || '',
          sync_type: scheduler.sync_type || '',
          sub_sync_type: scheduler.sub_sync_type || 'updated',
          direction: scheduler.direction || '',
          cron_expression: scheduler.cron_expression || '',
          is_active: scheduler.is_active ?? true,
          last_run: scheduler.last_run || null
        }));
      } catch (error) {
        console.error('Error fetching schedulers:', error);
        return [];
      }
    },
    // Add retry and error handling
    staleTime: 0
  });

  // Fetch schedule options
  const { data: allScheduleOptions = {} } = useQuery({
    queryKey: ['scheduleOptions'],
    queryFn: async () => {
      try {
        const response = await api.get('/schedulers/all-schedule-options');
        return response.data?.data || {};
      } catch (error) {
        console.error('Error fetching schedule options:', error);
        return {};
      }
    },
    refetchOnWindowFocus: false
  });

  // Update schedule options when allScheduleOptions changes
  useEffect(() => {
    setScheduleOptions(allScheduleOptions);
  }, [allScheduleOptions]);

  // Function to get current schedule options based on sync type and sub sync type
  const getCurrentScheduleOptions = (syncType, subSyncType) => {
    if (!syncType || !subSyncType || !scheduleOptions[syncType]) {
      return [];
    }
    return scheduleOptions[syncType][subSyncType] || [];
  };

  // Function to get schedule label from cron expression
  const getScheduleLabelFromCron = (cronExpression, syncType, subSyncType) => {
    if (!cronExpression || !syncType || !subSyncType || !scheduleOptions[syncType]) {
      return cronExpression || 'Unknown';
    }

    const options = scheduleOptions[syncType][subSyncType] || [];
    const matchingOption = options.find(opt => opt.cron === cronExpression);

    return matchingOption ? matchingOption.label : cronExpression;
  };

  // Function to update form values and original values together
  const updateFormValues = (newValues) => {
    formik.setValues(newValues);
    setOriginalFormValues(JSON.parse(JSON.stringify(newValues)));
    setHasFormChanges(false);
  };

  // Check if all types exist
  useEffect(() => {
    if (schedulers && schedulers.length > 0) {
      const existingSyncTypeSubTypeCombos = schedulers.map(s => ({
        syncType: s.sync_type?.toLowerCase(),
        subSyncType: s.sub_sync_type?.toLowerCase() || 'updated'
      }));

      const allPossibleCombos = [
        { syncType: 'product', subSyncType: 'updated' },
        { syncType: 'product', subSyncType: 'whole' },
        { syncType: 'tracking', subSyncType: 'updated' },
        { syncType: 'tracking', subSyncType: 'whole' },
        { syncType: 'customer', subSyncType: 'updated' },
        { syncType: 'customer', subSyncType: 'whole' },
        { syncType: 'price', subSyncType: 'updated' },
        { syncType: 'price', subSyncType: 'whole' }
      ];

      const usedCombos = existingSyncTypeSubTypeCombos.length;
      const totalCombos = allPossibleCombos.length;

      setAllTypesUsed(usedCombos >= totalCombos);
    } else {
      setAllTypesUsed(false);
    }
  }, [schedulers]);

  // Create scheduler mutation
  const createMutation = useMutation({
    mutationFn: async (schedulerData) => {
      // Transform the data to match API expectations - using camelCase as expected by backend
      const transformedData = {
        name: schedulerData.name,
        description: schedulerData.description || '',
        isActive: true,  // Default to true for new schedulers
        syncType: schedulerData.syncType,
        subSyncType: schedulerData.subSyncType || 'updated',
        schedule: schedulerData.schedule,
        direction: schedulerData.direction
      };

      const response = await api.post('/schedulers', transformedData);
      return Array.isArray(response.data) ? response.data[0] :
        response.data?.data ? response.data.data :
          response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulers'] });
      setIsDialogOpen(false);
      formik.resetForm();
      setHasFormChanges(false);
      toast.success('Scheduler created successfully');
    },
    onError: (error) => {
      console.error('Create scheduler error:', error);
      toast.error(`Error creating scheduler: ${error.response?.data?.message || error.message}`);
    }
  });

  // Update scheduler mutation
  const updateMutation = useMutation({
    mutationFn: async (schedulerData) => {
      // Transform the data to match API expectations - using camelCase as expected by backend
      const transformedData = {
        name: schedulerData.name,
        description: schedulerData.description || '',
        isActive: Boolean(schedulerData.isActive),  // Use camelCase for API
        syncType: schedulerData.syncType,           // Use camelCase for API
        subSyncType: schedulerData.subSyncType || 'updated',  // Use camelCase for API
        schedule: schedulerData.schedule,  // Use camelCase for API
        direction: schedulerData.direction
      };

      const response = await api.put(`/schedulers/${schedulerData.id}`, transformedData);
      return Array.isArray(response.data) ? response.data[0] :
        response.data?.data ? response.data.data :
          response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulers'] });
      setIsDialogOpen(false);
      formik.resetForm();
      setHasFormChanges(false);
      toast.success('Scheduler updated successfully');
    },
    onError: (error) => {
      console.error('Update scheduler error:', error);
      toast.error(`Error updating scheduler: ${error.response?.data?.message || error.message}`);
    }
  });

  // Delete scheduler mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/schedulers/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulers'] });
      setIsDeleteDialogOpen(false);
      setSelectedScheduler(null);
      toast.success('Scheduler deleted successfully');
    },
    onError: (error) => {
      console.error('Delete scheduler error:', error);
      toast.error(`Error deleting scheduler: ${error.response?.data?.message || error.message}`);
    }
  });

  // Toggle scheduler mutation with optimistic updates
  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }) => {
      setChangingStatus(id); // Set the changing status
      const response = await api.patch(`/schedulers/${id}/toggle`, { isActive });
      return Array.isArray(response.data) ? response.data[0] :
        response.data?.data ? response.data.data :
          response.data;
    },
    onMutate: async ({ id, isActive }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['schedulers'] });

      // Snapshot the previous value
      const previousSchedulers = queryClient.getQueryData(['schedulers']);

      // Optimistically update to the new value
      queryClient.setQueryData(['schedulers'], old => {
        return old.map(scheduler => {
          if (scheduler.id === id) {
            return { ...scheduler, is_active: isActive };
          }
          return scheduler;
        });
      });

      // Return a context object with the snapshotted value
      return { previousSchedulers };
    },
    onSuccess: (data, variables) => {
      const status = variables.isActive ? 'enabled' : 'disabled';
      toast.success(`Scheduler ${status} successfully`);
      setChangingStatus(null); // Reset changing status
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['schedulers'], context.previousSchedulers);
      console.error('Toggle scheduler error:', err);
      toast.error(`Error toggling scheduler: ${err.response?.data?.message || err.message}`);
      setChangingStatus(null); // Reset changing status
    },
    onSettled: () => {
      // Always refetch after error or success to make sure our optimistic update is correct
      queryClient.invalidateQueries({ queryKey: ['schedulers'] });
    }
  });

  // Run scheduler mutation
  const runMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/schedulers/${id}/run`);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate the schedulers query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['schedulers'] });

      // Update the single scheduler in the cache to immediately show updated last run time
      if (data && data.data) {
        queryClient.setQueryData(['schedulers'], (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(scheduler =>
            scheduler.id === data.data.id ? {
              ...scheduler,
              last_run: data.data.last_run
            } : scheduler
          );
        });
      }

      setIsRunning(null);
      toast.success('Scheduler run initiated successfully');
    },
    onError: (error) => {
      console.error('Run scheduler error:', error);
      setIsRunning(null);

      // Check if the error response includes updated scheduler data (when scheduler was disabled)
      if (error.response?.data?.data) {
        const updatedScheduler = error.response.data.data;

        // Update the scheduler in the cache to reflect the disabled status
        queryClient.setQueryData(['schedulers'], (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(scheduler =>
            scheduler.id === updatedScheduler.id ? {
              ...scheduler,
              is_active: updatedScheduler.is_active,
              last_run: updatedScheduler.last_run
            } : scheduler
          );
        });

        // Show the error message
        toast.error(error.response.data.message);
      } else {
        // Handle regular errors
        toast.error(`Error running scheduler: ${error.response?.data?.message || error.message}`);
      }
    }
  });

  // Form handling
  const formik = useFormik({
    initialValues: {
      id: '',
      name: '',
      description: '',
      schedule: '',
      syncType: '',
      subSyncType: 'updated',
      direction: 'erp_to_shopify',
      isActive: true
    },
    validationSchema: schedulerSchema,
    onSubmit: (values) => {
      // Transform values before submission
      const submissionData = {
        ...values,
        // Ensure these fields are not undefined/null
        name: values.name || '',
        description: values.description || '',
        syncType: values.syncType || '',
        schedule: values.schedule || '', // Ensure schedule is included
        direction: values.direction || 'erp_to_shopify',
        isActive: Boolean(values.isActive)
      };

      if (values.id) {
        updateMutation.mutate(submissionData);
      } else {
        createMutation.mutate(submissionData);
      }
    }
  });

  // Update current schedule options when form values change
  useEffect(() => {
    const options = getCurrentScheduleOptions(formik.values.syncType, formik.values.subSyncType);
    setCurrentScheduleOptions(options);

    // Clear schedule if current options don't include the selected schedule
    if (formik.values.schedule && !options.find(opt => opt.value === formik.values.schedule)) {
      formik.setFieldValue('schedule', '');
    }
  }, [formik.values.syncType, formik.values.subSyncType, scheduleOptions]);

  // Additional effect to handle schedule options loading when editing
  useEffect(() => {
    if (selectedScheduler && Object.keys(scheduleOptions).length > 0) {
      // Re-run schedule detection when options are loaded
      const syncTypeOptions = scheduleOptions[selectedScheduler.sync_type];
      if (syncTypeOptions && syncTypeOptions[selectedScheduler.sub_sync_type]) {
        const options = syncTypeOptions[selectedScheduler.sub_sync_type];
        const matchingOption = options.find(opt => opt.cron === selectedScheduler.cron_expression);
        if (matchingOption && formik.values.schedule !== matchingOption.value) {
          formik.setFieldValue('schedule', matchingOption.value);

          // Update original form values to prevent false change detection
          const updatedOriginalValues = {
            ...originalFormValues,
            schedule: matchingOption.value
          };
          setOriginalFormValues(updatedOriginalValues);
        }
      }
    }
  }, [scheduleOptions, selectedScheduler]);

  // Track changes compared to original form values
  useEffect(() => {
    if (Object.keys(originalFormValues).length > 0 && formik.values) {
      // Compare individual fields instead of entire objects to avoid false positives
      const hasChanges =
        formik.values.name !== originalFormValues.name ||
        formik.values.description !== originalFormValues.description ||
        formik.values.syncType !== originalFormValues.syncType ||
        formik.values.subSyncType !== originalFormValues.subSyncType ||
        formik.values.schedule !== originalFormValues.schedule ||
        formik.values.direction !== originalFormValues.direction ||
        formik.values.isActive !== originalFormValues.isActive;

      setHasFormChanges(hasChanges);
    }
  }, [formik.values, originalFormValues]);

  // Handle dialog close
  const handleDialogClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      setIsDialogOpen(false);
      formik.resetForm();
      setHasFormChanges(false);
    }
  };

  // Handle edit button click
  const handleEdit = (scheduler) => {
    if (!updateMutation.isPending && !createMutation.isPending) {
      setSelectedScheduler(scheduler);

      // Find the schedule value from the cron expression
      let scheduleValue = '';
      if (scheduler.sync_type && scheduler.sub_sync_type && scheduler.cron_expression) {
        // Use scheduleOptions directly instead of getCurrentScheduleOptions
        const syncTypeOptions = scheduleOptions[scheduler.sync_type];
        if (syncTypeOptions && syncTypeOptions[scheduler.sub_sync_type]) {
          const options = syncTypeOptions[scheduler.sub_sync_type];
          const matchingOption = options.find(opt => opt.cron === scheduler.cron_expression);
          if (matchingOption) {
            scheduleValue = matchingOption.value;
          } else {
            // If no matching option found, try to find by value
            const optionByValue = options.find(opt => opt.value === scheduler.cron_expression);
            if (optionByValue) {
              scheduleValue = optionByValue.value;
            }
          }
        }
      }

      // Map snake_case to camelCase when setting form values
      const formValues = {
        id: scheduler.id,
        name: scheduler.name,
        description: scheduler.description || '',
        schedule: scheduleValue,  // Set the schedule value
        syncType: scheduler.sync_type || '',  // Map snake_case to camelCase
        subSyncType: scheduler.sub_sync_type || 'updated',  // Map snake_case to camelCase
        direction: scheduler.direction || '',
        isActive: Boolean(scheduler.is_active)  // Ensure boolean and map snake_case to camelCase
      };

      updateFormValues(formValues);
      setIsDialogOpen(true);
    }
  };

  // Handle add button click
  const handleAdd = () => {
    if (!updateMutation.isPending && !createMutation.isPending) {
      setSelectedScheduler(null);
      const formValues = {
        id: '',
        name: '',
        description: '',
        schedule: '',
        syncType: '',
        subSyncType: 'updated',
        direction: 'erp_to_shopify',
        isActive: true
      };
      updateFormValues(formValues);
      setIsDialogOpen(true);
    }
  };

  // Handle delete button click
  const handleDelete = (scheduler) => {
    if (!deleteMutation.isPending) {
      setSelectedScheduler(scheduler);
      setIsDeleteDialogOpen(true);
    }
  };

  // Handle toggle switch
  const handleToggle = (scheduler) => {
    const newStatus = !scheduler.is_active;
    toggleMutation.mutate({
      id: scheduler.id,
      isActive: newStatus
    });
  };

  // Handle run button click
  const handleRun = (id) => {
    if (!runMutation.isPending) {
      setIsRunning(id);
      runMutation.mutate(id);
    }
  };

  // Filter available sync types based on existing ones (exclude ones already in use unless editing that type)
  const getAvailableSyncTypes = () => {
    if (!schedulers) return [];

    const existingSyncTypeSubTypeCombos = schedulers.map(s => ({
      syncType: s.sync_type?.toLowerCase() || '',
      subSyncType: s.sub_sync_type?.toLowerCase() || 'updated'
    }));

    const allSyncTypes = [
      { value: 'product', label: 'Product' },
      { value: 'tracking', label: 'Tracking' },
      { value: 'customer', label: 'Customer' },
      { value: 'price', label: 'Price' }
    ];

    if (selectedScheduler) {
      // When editing, show all types except ones already used by OTHER schedulers with the same sub_sync_type
      return allSyncTypes.filter(type => {
        const currentCombo = {
          syncType: type.value.toLowerCase(),
          subSyncType: formik.values.subSyncType?.toLowerCase() || 'updated'
        };

        const isUsedByOther = existingSyncTypeSubTypeCombos.some(combo =>
          combo.syncType === currentCombo.syncType &&
          combo.subSyncType === currentCombo.subSyncType &&
          !(combo.syncType === selectedScheduler.sync_type.toLowerCase() &&
            combo.subSyncType === selectedScheduler.sub_sync_type?.toLowerCase())
        );

        return !isUsedByOther;
      });
    } else {
      // When creating new, only show unused combinations
      return allSyncTypes.filter(type => {
        const currentCombo = {
          syncType: type.value.toLowerCase(),
          subSyncType: formik.values.subSyncType?.toLowerCase() || 'updated'
        };

        return !existingSyncTypeSubTypeCombos.some(combo =>
          combo.syncType === currentCombo.syncType &&
          combo.subSyncType === currentCombo.subSyncType
        );
      });
    }
  };

  return (
    <Box>

      <ConfigurationWarning justShowContentWhenNoConfig={true}>
        <PageHeader
          title="Schedulers"
          subtitle="Manage automated sync jobs"
        />
      </ConfigurationWarning>

      <ConfigurationWarning>
        <PageHeader
          title="Schedulers"
          subtitle="Manage automated sync jobs"
          button={
            isAdmin
              ? {
                text: 'Add New',
                onClick: handleAdd,
                icon: <AddIcon />,
                disabled: allTypesUsed,
                tooltip: allTypesUsed ? 'All scheduler types already exist' : ''
              }
              : null
          }
        />

        {isLoading ? (
          <Grid container spacing={3}>
            {[...Array(4)].map((_, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="60%" height={32} />
                      <Skeleton variant="text" width="40%" height={24} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={24} />
                      </Grid>
                      <Grid item xs={6}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={24} />
                      </Grid>
                      <Grid item xs={6}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={24} />
                      </Grid>
                      <Grid item xs={6}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={24} />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton variant="circular" width={40} height={40} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : schedulers && schedulers.length > 0 ? (
          <Grid container spacing={3}>
            {schedulers.map((scheduler) => (
              <Grid item xs={12} md={6} key={scheduler.id}>
                <Card>
                  <CardContent>
                    {changingStatus === scheduler.id ? (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Skeleton variant="text" width="60%" height={32} />
                          <Skeleton variant="text" width="40%" height={24} />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Skeleton variant="text" width="80%" height={24} />
                            <Skeleton variant="text" width="60%" height={24} />
                          </Grid>
                          <Grid item xs={6}>
                            <Skeleton variant="text" width="80%" height={24} />
                            <Skeleton variant="text" width="60%" height={24} />
                          </Grid>
                          <Grid item xs={6}>
                            <Skeleton variant="text" width="80%" height={24} />
                            <Skeleton variant="text" width="60%" height={24} />
                          </Grid>
                          <Grid item xs={6}>
                            <Skeleton variant="text" width="80%" height={24} />
                            <Skeleton variant="text" width="60%" height={24} />
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                          <Skeleton variant="circular" width={40} height={40} />
                          <Skeleton variant="circular" width={40} height={40} />
                          <Skeleton variant="circular" width={40} height={40} />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {scheduler.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {scheduler.description || 'No description'}
                            </Typography>
                          </Box>
                          {isAdmin ? (
                            <>
                              <Switch
                                checked={Boolean(scheduler.is_active)}
                                onChange={() => handleToggle(scheduler)}
                                disabled={!isAdmin || changingStatus === scheduler.id || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || runMutation.isPending || toggleMutation.isPending}
                              />
                            </>
                          ) : (<></>)}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Sync Type
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {scheduler.sync_type ?
                                (scheduler.sync_type.charAt(0).toUpperCase() + scheduler.sync_type.slice(1)) :
                                'Unknown'
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Sub Sync Type
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {scheduler.sub_sync_type ?
                                (scheduler.sub_sync_type === 'whole' ? 'Whole Sync' : 'Updated Items') :
                                'Updated Items'
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Direction
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {scheduler.direction === 'shopify_to_erp' ? 'Shopify to ERP' : 'ERP to Shopify'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Schedule
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {getScheduleLabelFromCron(scheduler.cron_expression, scheduler.sync_type, scheduler.sub_sync_type)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Status
                            </Typography>
                            <StatusBadge status={scheduler.is_active ? 'active' : 'inactive'} />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Last Run
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {formatDate(scheduler.last_run) || 'Not run yet'}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                          {isAdmin && (
                            <>
                              <Tooltip title="Edit">
                                <span>
                                  <IconButton
                                    onClick={() => handleEdit(scheduler)}
                                    disabled={
                                      deleteMutation.isPending ||
                                      createMutation.isPending ||
                                      updateMutation.isPending
                                    }
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <span>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDelete(scheduler)}
                                    disabled={
                                      deleteMutation.isPending ||
                                      createMutation.isPending ||
                                      updateMutation.isPending
                                    }
                                  >
                                    {/* {deleteMutation.isPending && deleteMutation.variables === scheduler.id ? (
                                      <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: 'error.main' }} />
                                    ) : ( */}
                                    <DeleteIcon disabled={deleteMutation.isPending && deleteMutation.variables === scheduler.id} />
                                    {/* )} */}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          {isAdmin ? (
                            <>
                              <Tooltip title="Run Now">
                                <span>
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleRun(scheduler.id)}
                                    disabled={
                                      !isAdmin ||
                                      !scheduler.is_active ||
                                      runMutation.isPending ||
                                      isRunning === scheduler.id
                                    }
                                  >
                                    {isRunning === scheduler.id ? (
                                      <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: 'primary.main' }} />
                                    ) : (
                                      <PlayArrowIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : (<></>)}
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ScheduleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Schedulers Found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {isAdmin
                    ? 'Create a scheduler to automate sync operations between Shopify and your ERP system.'
                    : 'No schedulers have been configured yet. Please contact an administrator.'}
                </Typography>
                {isAdmin && (
                  <Tooltip title={allTypesUsed ? 'All scheduler types already exist' : ''}>
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        disabled={allTypesUsed}
                      >
                        Add Scheduler
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ py: 2, px: 3, fontSize: 18}}>
            {selectedScheduler ? 'Edit Scheduler' : 'Add Scheduler'}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={isMobile ? 1 : 3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Name"
                  placeholder="e.g., Daily Product Sync"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description (Optional)"
                  placeholder="e.g., Syncs products from Shopify to ERP every day at midnight"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  id="syncType"
                  name="syncType"
                  label="Sync Type"
                  value={formik.values.syncType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.syncType && Boolean(formik.errors.syncType)}
                  helperText={formik.touched.syncType && formik.errors.syncType}
                >
                  {getAvailableSyncTypes().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  id="subSyncType"
                  name="subSyncType"
                  label="Sub Sync Type"
                  value={formik.values.subSyncType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.subSyncType && Boolean(formik.errors.subSyncType)}
                  helperText={formik.touched.subSyncType && formik.errors.subSyncType}
                >
                  <MenuItem value="updated">Updated Items</MenuItem>
                  <MenuItem value="whole">Whole Sync</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  id="direction"
                  name="direction"
                  label="Direction"
                  value={formik.values.direction}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.direction && Boolean(formik.errors.direction)}
                  helperText={formik.touched.direction && formik.errors.direction}
                >
                  <MenuItem value="erp_to_shopify">ERP to Shopify</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  id="schedule"
                  name="schedule"
                  label="Schedule"
                  value={formik.values.schedule || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.schedule && Boolean(formik.errors.schedule)}
                  helperText={formik.touched.schedule && formik.errors.schedule}
                  disabled={!formik.values.syncType || !formik.values.subSyncType}
                >
                  {currentScheduleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDialogClose}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !formik.isValid ||
                !hasFormChanges ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              onClick={() => {
                if (formik.isValid && hasFormChanges) {
                  if (selectedScheduler) {
                    updateMutation.mutate(formik.values);
                  } else {
                    createMutation.mutate(formik.values);
                  }
                }
              }}
            >
              {createMutation.isPending || updateMutation.isPending ? (
                selectedScheduler ? 'Updating...' : 'Creating...'
              ) : selectedScheduler ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={isDeleteDialogOpen}
          title="Delete Scheduler"
          message={`Are you sure you want to delete the scheduler "${selectedScheduler?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmColor="error"
          onConfirm={() => {
            if (selectedScheduler && !deleteMutation.isPending) {
              deleteMutation.mutate(selectedScheduler.id);
            }
          }}
          onCancel={() => {
            if (!deleteMutation.isPending) {
              setIsDeleteDialogOpen(false);
              setSelectedScheduler(null);
            }
          }}
          isLoading={deleteMutation.isPending}
        />

      </ConfigurationWarning>
    </Box>
  );
}
